import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useWorkshop } from '../hooks/api-hooks';
import { api } from '../lib/api';
import { ApiError } from '../lib/api-client';
import { env } from '../lib/env';
import { ErrorState, Loading } from '../components/States';
import { formatDateTime } from '../lib/format';
import type { WorkshopRegistrationInput } from '../lib/types';
import { TurnstileWidget } from '../components/TurnstileWidget';

export function WorkshopPage() {
  const { slug } = useParams();
  const workshop = useWorkshop(slug);
  const [sessionId, setSessionId] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Public endpoint: registration works signed out, and the access token is
  // attached automatically when a session exists.
  const register = useMutation<unknown, ApiError, WorkshopRegistrationInput>({
    mutationFn: (input) => api.registerWorkshop(slug as string, input),
  });

  if (workshop.isPending) return <Loading />;
  if (workshop.error) return <ErrorState error={workshop.error} />;
  if (!workshop.data) return null;

  const data = workshop.data;
  const now = Date.now();
  const notOpenYet =
    data.registrationOpensAt !== null && new Date(data.registrationOpensAt).getTime() > now;
  const closed =
    data.registrationClosesAt !== null && new Date(data.registrationClosesAt).getTime() <= now;
  const availableSessions = data.sessions.filter((session) => {
    const full = session.capacity !== null && session.registeredCount >= session.capacity;
    return !full && new Date(session.startsAt).getTime() > now;
  });
  const registrationUnavailable = notOpenYet || closed || availableSessions.length === 0;
  const registrationStatus = notOpenYet
    ? `Đăng ký mở lúc ${formatDateTime(data.registrationOpensAt as string)}.`
    : closed
      ? 'Thời gian đăng ký đã kết thúc.'
      : data.sessions.length === 0
        ? 'Workshop chưa mở lịch đăng ký.'
        : availableSessions.length === 0
          ? 'Các buổi hiện tại đã đầy hoặc đã diễn ra.'
          : null;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    register.mutate({
      sessionId,
      fullName: String(form.get('fullName') ?? ''),
      email: String(form.get('email') ?? ''),
      phone: String(form.get('phone') ?? ''),
      organization: String(form.get('organization') ?? '') || undefined,
      note: String(form.get('note') ?? '') || undefined,
      source: 'website',
      turnstileToken: turnstileToken || undefined,
    });
  }

  return (
    <article className="workshop-detail">
      <header className="workshop-hero">
        <div>
          <h1>{data.title}</h1>
          {data.shortDescription && <p className="lead">{data.shortDescription}</p>}
        </div>
        {data.bannerUrl && (
          <img src={data.bannerUrl} alt={`Ảnh giới thiệu ${data.title}`} className="hero-image" />
        )}
      </header>

      <div className="workshop-layout">
        <section className="workshop-content">
          {data.description && (
            <>
              <h2>Nội dung workshop</h2>
              <p>{data.description}</p>
            </>
          )}
          <dl className="workshop-meta panel">
            {data.speakerName && (
              <>
                <dt>Diễn giả</dt>
                <dd>{data.speakerName}</dd>
              </>
            )}
            <dt>Hình thức</dt>
            <dd>{data.venueType}</dd>
            {data.venueAddress && (
              <>
                <dt>Địa điểm</dt>
                <dd>{data.venueAddress}</dd>
              </>
            )}
            {data.meetingPlatform && (
              <>
                <dt>Nền tảng</dt>
                <dd>{data.meetingPlatform}</dd>
              </>
            )}
          </dl>
          {data.speakerBio && (
            <section className="speaker-bio">
              <h2>Về diễn giả</h2>
              <p>{data.speakerBio}</p>
            </section>
          )}
        </section>

        <section className="workshop-registration panel" aria-labelledby="registration-title">
          <h2 id="registration-title">Đăng ký tham dự</h2>
          {registrationStatus && (
            <p className="notice" role="status">
              {registrationStatus}
            </p>
          )}

          {register.isSuccess ? (
            <p className="success" role="status">
              Đăng ký thành công. Thông tin của bạn đã được ghi nhận.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="stack-form">
              <fieldset disabled={registrationUnavailable || register.isPending}>
                <legend>Chọn buổi phù hợp</legend>
                <div className="session-options">
                  {data.sessions.map((session) => {
                    const full =
                      session.capacity !== null && session.registeredCount >= session.capacity;
                    const hasPassed = new Date(session.startsAt).getTime() <= now;
                    const disabled = full || hasPassed;
                    return (
                      <label key={session.id} className="radio session-option">
                        <input
                          type="radio"
                          name="sessionId"
                          value={session.id}
                          checked={sessionId === session.id}
                          onChange={() => setSessionId(session.id)}
                          disabled={disabled}
                          required
                        />
                        <span>
                          <strong>{formatDateTime(session.startsAt)}</strong>
                          {session.endsAt && (
                            <span className="muted"> đến {formatDateTime(session.endsAt)}</span>
                          )}
                          {session.capacity !== null && (
                            <span className="session-capacity muted">
                              {full
                                ? 'Đã đủ chỗ'
                                : `Còn ${Math.max(0, session.capacity - session.registeredCount)} chỗ`}
                            </span>
                          )}
                          {hasPassed && <span className="session-capacity muted">Đã diễn ra</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <label htmlFor="fullName">Họ và tên</label>
                <input id="fullName" name="fullName" autoComplete="name" required minLength={2} maxLength={120} />

                <label htmlFor="email">Email</label>
                <input id="email" name="email" type="email" autoComplete="email" required />

                <label htmlFor="phone">Số điện thoại</label>
                <input id="phone" name="phone" type="tel" autoComplete="tel" required minLength={6} maxLength={30} />

                <label htmlFor="organization">Đơn vị (không bắt buộc)</label>
                <input id="organization" name="organization" autoComplete="organization" maxLength={200} />

                <label htmlFor="note">Ghi chú (không bắt buộc)</label>
                <textarea id="note" name="note" maxLength={1000} />

                {env.turnstileSiteKey && !registrationUnavailable && (
                  <TurnstileWidget
                    key={register.failureCount}
                    siteKey={env.turnstileSiteKey}
                    onTokenChange={setTurnstileToken}
                  />
                )}

                <button
                  type="submit"
                  disabled={!sessionId || Boolean(env.turnstileSiteKey && !turnstileToken)}
                >
                  {register.isPending ? 'Đang gửi…' : 'Đăng ký'}
                </button>
              </fieldset>
            </form>
          )}
          {register.error && <ErrorState error={register.error} />}
        </section>
      </div>
    </article>
  );
}
